import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import Input from '../../components/ui/Input.jsx';
import { customersApiService, whoisService } from '../../services';

const WHOIS_API_URL = process.env.REACT_APP_WHOIS_API_URL;
const DEFAULT_WHOIS_API_TOKEN = process.env.REACT_APP_WHOIS_API_TOKEN;

function Whois({ tabColors = { bgcolor: 'bg-yellow-300', nocolor: 'bg-yellow-100', hovercolor: 'hover:bg-yellow-200' } }) {
  const { id } = useParams();
  const [, setTicket] = useState(null);
  const [apiToken, setApiToken] = useState('');
  const [tokenSaved, setTokenSaved] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [tokenSuccessMessage, setTokenSuccessMessage] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [whoisData, setWhoisData] = useState(null);

  useEffect(() => {
    if (id) {
      customersApiService.getTicketById(id)
        .then(async data => {
          // Fetch fresh customer data to avoid stale cached names
          let customerName = data.customer_name;
          try {
            const customer = await customersApiService.getCustomerByNumber(data.customer_number);
            if (customer && customer.customer_name) {
              customerName = customer.customer_name;
            }
          } catch (err) {
            console.warn('Could not fetch fresh customer data, using cached name:', err);
          }

          setTicket(data);
          const pageTitle = `${data.ticket_number} - ${customerName} - ${data.issue_with}`;
          document.title = pageTitle;

          // Check if token already exists
          if (data.whois_api_token) {
            setApiToken(data.whois_api_token);
            setTokenSaved(true);
          }
        })
        .catch(err => {
          console.error('Error fetching ticket:', err);
          document.title = 'SSK - Support Swiss Knife';
        });
    } else {
      document.title = 'SSK - Support Swiss Knife';
    }

    return () => {
      document.title = 'SSK - Support Swiss Knife';
    };
  }, [id]);

  const handleSaveToken = async () => {
    if (!apiToken || apiToken.trim() === '') {
      setTokenError('Please enter an API token');
      return;
    }

    setTokenLoading(true);
    setTokenError('');
    setTokenSuccessMessage('');

    try {
      await whoisService.updateToken(id, apiToken);
      setTokenSaved(true);
      setTokenSuccessMessage('API Token saved successfully!');
      setTimeout(() => setTokenSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving token:', err);
      setTokenError(
        err.response?.data?.error || 'Failed to save API token. Please try again.'
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const handleTokenKeyDown = e => {
    if (e.key === 'Enter' && !tokenSaved) {
      handleSaveToken();
    }
  };

  const handleInputChange = e => {
    setIpAddress(e.target.value);
    setError('');
  };

  const handleLookup = async () => {
    if (!ipAddress.trim()) {
      setError('Please enter an IP address');
      return;
    }

    // Basic IP format validation
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ipAddress.trim())) {
      setError('Invalid IP address format');
      return;
    }

    setLoading(true);
    setError('');
    setWhoisData(null);

    // Use the saved token if available, otherwise fall back to env variable
    const tokenToUse = apiToken || DEFAULT_WHOIS_API_TOKEN;

    try {
      const response = await fetch(WHOIS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenToUse}`
        },
        body: JSON.stringify({ ip: ipAddress.trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWhoisData(data);
      } else {
        setError(data.message || 'Failed to lookup IP address');
      }
    } catch (err) {
      console.error('WHOIS lookup error:', err);
      setError('Network error: Unable to perform WHOIS lookup');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleLookup();
    }
  };

  const handleClearInput = () => {
    setIpAddress('');
    setError('');
    setWhoisData(null);
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text).then(() => {
      const messageElement = document.getElementById('copy-message');
      if (messageElement) {
        messageElement.textContent = 'Copied to clipboard!';
        messageElement.classList.remove('hidden');
        setTimeout(() => {
          messageElement.textContent = '';
          messageElement.classList.add('hidden');
        }, 2000);
      }
    });
  };

  return (
    <div className='h-screen flex flex-col items-center px-4 pt-5 overflow-y-auto'>
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-2xl mb-8">
        <div className="flex items-center justify-center mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-12 h-12 text-teal-600 mr-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
          <h1 className="font-bold text-3xl text-gray-800">WHOIS Lookup</h1>
        </div>

        {/* API Token Configuration Section */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">API Token Configuration</h2>

          <div className="flex gap-3 items-start">
            <div className="flex-1">
              <label
                htmlFor="apiToken"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                API Token
              </label>
              <input
                type="text"
                id="apiToken"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                onKeyDown={handleTokenKeyDown}
                disabled={tokenSaved}
                placeholder="Enter your WHOIS API token"
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-teal-400 focus:border-teal-400 ${
                  tokenSaved
                    ? 'bg-gray-100 cursor-not-allowed'
                    : 'bg-white'
                }`}
              />
            </div>

            <div className="pt-7">
              {!tokenSaved ? (
                <button
                  onClick={handleSaveToken}
                  disabled={tokenLoading}
                  className={`px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                    tokenLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {tokenLoading ? 'Saving...' : 'Save Token'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setTokenSaved(false);
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Edit Token
                </button>
              )}
            </div>
          </div>

          {/* Token Success/Error Messages */}
          {tokenSuccessMessage && (
            <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {tokenSuccessMessage}
            </div>
          )}

          {tokenError && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {tokenError}
            </div>
          )}
        </div>

        <div className="mb-6">
          <label htmlFor="ipAddress" className="block text-sm font-semibold text-gray-700 mb-2">
            IP Address
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="ipAddress"
                value={ipAddress}
                placeholder="Enter IP Address (e.g., 8.8.8.8)"
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className="w-full pr-10"
                disabled={loading}
              />
              {ipAddress && (
                <button
                  onClick={handleClearInput}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Clear input"
                  type="button"
                >
                  <img
                    src="/img/trash-can.svg"
                    alt="Clear"
                    className="w-5 h-5 opacity-60 hover:opacity-100"
                  />
                </button>
              )}
            </div>
            <button
              onClick={handleLookup}
              disabled={loading}
              className={`px-6 py-2 bg-teal-600 text-white font-semibold border-b-4 rounded hover:bg-teal-500 border-teal-800 hover:border-teal-700 flex items-center ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading...
                </>
              ) : (
                'Lookup'
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {whoisData && whoisData.success && (
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">WHOIS Information</h2>
              {whoisData.data.logoUrl && (
                <img
                  src={whoisData.data.logoUrl}
                  alt="ISP Logo"
                  className="h-12 max-w-[120px] object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>

            <div className="space-y-3">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600 font-semibold mb-1">IP Address:</p>
                <p className="text-lg text-gray-800 font-mono">{whoisData.data.ip}</p>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600 font-semibold mb-1">Organization:</p>
                <p className="text-gray-800 whitespace-pre-wrap">{whoisData.data.organization}</p>
                <button
                  onClick={() => copyToClipboard(whoisData.data.organization)}
                  className="mt-2 px-3 py-1 bg-teal-600 text-white text-sm font-semibold rounded hover:bg-teal-500 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                    />
                  </svg>
                  Copy
                </button>
                <p
                  id="copy-message"
                  className="bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
                ></p>
              </div>

              {whoisData.meta && whoisData.meta.rateLimit && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm">
                  <p className="text-gray-600">
                    <span className="font-semibold">Rate Limit:</span>{' '}
                    {whoisData.meta.rateLimit.remaining} / {whoisData.meta.rateLimit.limit} remaining
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Powered by WHOIS API</p>
        </div>
      </div>
    </div>
  );
}

export default Whois;
